import { readCssVar } from './css-vars';

export type ThemeMode =
  | 'light'
  | 'ocean'
  | 'emerald'
  | 'violet'
  | 'rose'
  | 'dark';

export type ThemeOption = {
  id: ThemeMode;
  label: string;
  color: string;
  dark: boolean;
};

export const THEME_OPTIONS: ThemeOption[] = [
  { id: 'light', label: 'Classic Navy', color: '#1d4ed8', dark: false },
  { id: 'ocean', label: 'Ocean Blue', color: '#0369a1', dark: false },
  { id: 'emerald', label: 'Executive Teal', color: '#0f766e', dark: false },
  { id: 'violet', label: 'Slate Indigo', color: '#4f46e5', dark: false },
  { id: 'rose', label: 'Graphite', color: '#334155', dark: false },
  { id: 'dark', label: 'Charcoal Dark', color: '#60a5fa', dark: true },
];

export const THEME_STORAGE_KEY = 'jo-theme';
export const DEFAULT_THEME: ThemeMode = 'ocean';

export const getThemeStorageKey = (userId?: string | null): string => {
  const normalizedUserId = userId?.trim();
  return normalizedUserId
    ? `${THEME_STORAGE_KEY}:${encodeURIComponent(normalizedUserId)}`
    : THEME_STORAGE_KEY;
};

export const resolveTheme = (stored: string | null): ThemeMode => {
  return THEME_OPTIONS.some(({ id }) => id === stored)
    ? (stored as ThemeMode)
    : DEFAULT_THEME;
};

export const isDarkTheme = (theme: ThemeMode): boolean => theme === 'dark';

export const getStoredTheme = (userId?: string | null): ThemeMode => {
  if (typeof window === 'undefined') {
    return DEFAULT_THEME;
  }

  const userTheme = window.localStorage.getItem(getThemeStorageKey(userId));
  const legacyTheme = userId
    ? window.localStorage.getItem(THEME_STORAGE_KEY)
    : null;

  return resolveTheme(userTheme ?? legacyTheme);
};

export const applyTheme = (theme: ThemeMode): void => {
  document.documentElement.setAttribute('data-theme', theme);
  document.documentElement.style.colorScheme = isDarkTheme(theme)
    ? 'dark'
    : 'light';

  const metaTheme = document.querySelector('meta[name="theme-color"]');
  if (metaTheme) {
    const fallback = isDarkTheme(theme) ? '#111827' : '#1d4ed8';
    const token = isDarkTheme(theme)
      ? readCssVar('bgStart', fallback)
      : readCssVar('primary', fallback);
    metaTheme.setAttribute('content', token);
  }
};

export const persistTheme = (
  theme: ThemeMode,
  userId?: string | null
): void => {
  window.localStorage.setItem(getThemeStorageKey(userId), theme);
  applyTheme(theme);
};
